import { RefObject, useEffect, useRef, type CSSProperties } from 'react';

export type ValueOverlayMode = 'off' | 'grayscale' | 'bands-3' | 'bands-5' | 'bands-8';

const MAX_PROCESSING_WIDTH = 1280;
const LIVE_FRAME_INTERVAL_MS = 1000 / 12;
const LOW_PASS_KERNEL = [1, 2, 1] as const;
const LOW_PASS_KERNEL_SUM = 4;
const LOW_PASS_RADIUS = 1;

interface LowPassBuffers {
  luminance: Uint8ClampedArray;
  horizontalBlur: Uint16Array;
}

function getBandCount(mode: ValueOverlayMode): number | null {
  if (mode === 'bands-3') return 3;
  if (mode === 'bands-5') return 5;
  if (mode === 'bands-8') return 8;
  return null;
}

function getSourceDimensions(source: HTMLVideoElement | HTMLImageElement) {
  if (source instanceof HTMLVideoElement) {
    return {
      width: source.videoWidth,
      height: source.videoHeight,
    };
  }

  return {
    width: source.naturalWidth,
    height: source.naturalHeight,
  };
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function createLowPassBuffers(pixelCount: number): LowPassBuffers {
  return {
    luminance: new Uint8ClampedArray(pixelCount),
    horizontalBlur: new Uint16Array(pixelCount),
  };
}

function quantizeValues(imageData: ImageData, bands: number, buffers: LowPassBuffers) {
  const { data, width, height } = imageData;
  const pixelCount = width * height;
  const { luminance, horizontalBlur } = buffers;

  for (let pixelIndex = 0, dataIndex = 0; pixelIndex < pixelCount; pixelIndex += 1, dataIndex += 4) {
    luminance[pixelIndex] = Math.round(
      0.2126 * data[dataIndex] +
      0.7152 * data[dataIndex + 1] +
      0.0722 * data[dataIndex + 2]
    );
  }

  for (let y = 0; y < height; y += 1) {
    const rowOffset = y * width;

    for (let x = 0; x < width; x += 1) {
      let value = 0;

      for (let kernelIndex = 0; kernelIndex < LOW_PASS_KERNEL.length; kernelIndex += 1) {
        const sampleX = clamp(x + kernelIndex - LOW_PASS_RADIUS, 0, width - 1);
        value += luminance[rowOffset + sampleX] * LOW_PASS_KERNEL[kernelIndex];
      }

      horizontalBlur[rowOffset + x] = value;
    }
  }

  for (let y = 0, dataIndex = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1, dataIndex += 4) {
      let value = 0;

      for (let kernelIndex = 0; kernelIndex < LOW_PASS_KERNEL.length; kernelIndex += 1) {
        const sampleY = clamp(y + kernelIndex - LOW_PASS_RADIUS, 0, height - 1);
        value += horizontalBlur[sampleY * width + x] * LOW_PASS_KERNEL[kernelIndex];
      }

      const brightness = value / (LOW_PASS_KERNEL_SUM * LOW_PASS_KERNEL_SUM);
      const band = Math.min(bands - 1, Math.floor((brightness / 256) * bands));
      const bandValue = Math.round((band / (bands - 1)) * 255);

      data[dataIndex] = bandValue;
      data[dataIndex + 1] = bandValue;
      data[dataIndex + 2] = bandValue;
    }
  }
}

interface ValueAnalysisOverlayProps {
  mode: ValueOverlayMode;
  videoRef: RefObject<HTMLVideoElement>;
  imageRef: RefObject<HTMLImageElement>;
  sourceType: 'video' | 'image';
  sourceKey: string | null;
  className?: string;
  style?: CSSProperties;
}

export function ValueAnalysisOverlay({
  mode,
  videoRef,
  imageRef,
  sourceType,
  sourceKey,
  className = 'absolute inset-0 z-[1] h-full w-full object-contain pointer-events-none',
  style,
}: ValueAnalysisOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const workCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const lowPassBuffersRef = useRef<LowPassBuffers | null>(null);

  useEffect(() => {
    const bands = getBandCount(mode);
    const outputCanvas = canvasRef.current;

    if (!bands || !outputCanvas) return;

    let animationFrame = 0;
    let lastFrameAt = 0;
    let cancelled = false;

    const renderFrame = () => {
      const source = sourceType === 'image' ? imageRef.current : videoRef.current;
      if (!source) return;

      if (source instanceof HTMLVideoElement && source.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
        return;
      }

      if (source instanceof HTMLImageElement && !source.complete) {
        return;
      }

      const sourceDimensions = getSourceDimensions(source);
      if (!sourceDimensions.width || !sourceDimensions.height) return;

      const scale = Math.min(1, MAX_PROCESSING_WIDTH / sourceDimensions.width);
      const width = Math.max(1, Math.round(sourceDimensions.width * scale));
      const height = Math.max(1, Math.round(sourceDimensions.height * scale));

      const workCanvas = workCanvasRef.current ?? document.createElement('canvas');
      workCanvasRef.current = workCanvas;

      if (workCanvas.width !== width) workCanvas.width = width;
      if (workCanvas.height !== height) workCanvas.height = height;
      if (outputCanvas.width !== width) outputCanvas.width = width;
      if (outputCanvas.height !== height) outputCanvas.height = height;

      const workContext = workCanvas.getContext('2d', { willReadFrequently: true });
      const outputContext = outputCanvas.getContext('2d');
      if (!workContext || !outputContext) return;

      workContext.drawImage(source, 0, 0, width, height);
      const imageData = workContext.getImageData(0, 0, width, height);
      const pixelCount = width * height;
      if (lowPassBuffersRef.current?.luminance.length !== pixelCount) {
        lowPassBuffersRef.current = createLowPassBuffers(pixelCount);
      }
      quantizeValues(imageData, bands, lowPassBuffersRef.current);
      outputContext.putImageData(imageData, 0, 0);
    };

    const renderLoop = (timestamp: number) => {
      if (cancelled) return;

      if (timestamp - lastFrameAt >= LIVE_FRAME_INTERVAL_MS) {
        renderFrame();
        lastFrameAt = timestamp;
      }

      animationFrame = requestAnimationFrame(renderLoop);
    };

    if (sourceType === 'image') {
      const image = imageRef.current;
      if (image?.complete) {
        renderFrame();
      } else {
        image?.addEventListener('load', renderFrame, { once: true });
        return () => {
          cancelled = true;
          image?.removeEventListener('load', renderFrame);
        };
      }
    } else {
      animationFrame = requestAnimationFrame(renderLoop);
    }

    return () => {
      cancelled = true;
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, [mode, videoRef, imageRef, sourceType, sourceKey]);

  if (!getBandCount(mode)) return null;

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ ...style, filter: 'blur(0.35px)' }}
      aria-hidden="true"
    />
  );
}
